import { Badge, Box, Button, Checkbox, Flex, HStack, Table, Tbody, Text, Th, Thead, Tr } from '@elkaso-app/web-design';
import { PriceImportRow } from 'apis/price-imports';
import { usePageParams } from 'hooks/use-page-params';
import { ImportRowLine } from 'pages/price-imports/components/import-row-line';
import { isHighConfidence } from 'pages/price-imports/utils/match-tier';
import {
  bucketOf,
  BUCKET_COLOR_SCHEME,
  BUCKET_LABEL,
  countBuckets,
  RowBucket,
} from 'pages/price-imports/utils/row-buckets';
import { useImportRowActions } from 'pages/price-imports/utils/use-import-row-actions';
import { useEffect, useMemo, useState } from 'react';
import type { PriceTier } from 'utils/price-units';

export interface RowReviewPanelProps {
  runId: number;
  rows: PriceImportRow[];
  isLoading: boolean;
  readOnly: boolean;
}

const BUCKETS: RowBucket[] = ['ready', 'decide', 'hold'];

/**
 * The review grid: three buckets over a dense table.
 *
 * A plain Chakra table rather than react-table. ExcelTable's own docblock says
 * it is a READ table, BaseTable wraps every row in dnd-kit for a drag-reorder
 * we do not want, and both existing edit grids in this repo
 * (exclusive-deals/editable-deals-grid and the family workspace variants grid)
 * independently reached the same conclusion.
 */
export const RowReviewPanel = ({ runId, rows, isLoading, readOnly }: RowReviewPanelProps) => {
  const { getPageParams, setPageParams } = usePageParams();
  const params = getPageParams();
  const bucket = (params.bucket as RowBucket) || 'decide';
  const expandedRowId = params.rowId ? Number(params.rowId) : null;

  const [selected, setSelected] = useState<Set<number>>(new Set());
  const { saveRow, setRowsStatus, flushPendingRefetch, isSaving } = useImportRowActions(runId);

  // Fire the deferred refetch on the way out, so the grid never re-sorts under
  // the operator mid-edit but the server's version still lands eventually.
  useEffect(() => flushPendingRefetch, [flushPendingRefetch]);

  const counts = useMemo(() => countBuckets(rows), [rows]);
  const visible = useMemo(() => rows.filter((row) => bucketOf(row.status) === bucket), [rows, bucket]);

  // Selection is scoped to what is on screen; changing bucket resets it, or an
  // operator would believe a bulk action hit rows they never saw.
  const changeBucket = (next: RowBucket) => {
    setSelected(new Set());
    setPageParams({ ...getPageParams(), bucket: next, rowId: undefined });
  };

  const toggle = (rowId: number) =>
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });

  const toggleAll = () =>
    setSelected((current) => (current.size === visible.length ? new Set() : new Set(visible.map((row) => row.id))));

  const commitPrice = (rowId: number, next: { price: number; tier: PriceTier | null }) =>
    saveRow(rowId, { dealPrice: next.price, ...(next.tier ? { dealUnit: next.tier } : {}) });

  const setStatus = (rowId: number, status: 'approved' | 'skipped' | 'needs_review') => saveRow(rowId, { status });

  // In the URL rather than component state, so a half-finished re-match
  // survives a refresh and can be linked to -- the same reason
  // top-products-panel.tsx keeps its expanded row in a param.
  const toggleExpand = (rowId: number) =>
    setPageParams({ ...getPageParams(), rowId: expandedRowId === rowId ? undefined : rowId });

  const pickMaster = async (rowId: number, masterProductId: number) => {
    await saveRow(rowId, { masterProductId });
    // Picking a product is the decision -- collapse and move on.
    setPageParams({ ...getPageParams(), rowId: undefined });
  };

  const bulk = async (status: 'approved' | 'skipped' | 'needs_review') => {
    if (selected.size === 0) return;
    await setRowsStatus([...selected], status);
    setSelected(new Set());
  };

  /**
   * Filter-side, not selection-side: "select 500 rows" is not a thing an
   * operator can do, and the high-confidence tiers (supplier code, prior link,
   * exact natural key) are exact by construction.
   */
  const acceptHighConfidence = async () => {
    const ids = rows
      .filter((row) => bucketOf(row.status) === 'decide' && isHighConfidence(row.matchTier))
      .map((row) => row.id);
    if (ids.length === 0) return;
    await setRowsStatus(ids, 'approved');
  };

  const highConfidenceCount = useMemo(
    () => rows.filter((row) => bucketOf(row.status) === 'decide' && isHighConfidence(row.matchTier)).length,
    [rows]
  );

  return (
    <Box>
      <Flex justify='space-between' align='center' mb='12px' gap='12px' wrap='wrap'>
        <HStack spacing='6px'>
          {BUCKETS.map((entry) => (
            <Button
              key={entry}
              size='sm'
              variant={bucket === entry ? 'solid' : 'outline'}
              colorScheme={bucket === entry ? BUCKET_COLOR_SCHEME[entry] : 'gray'}
              onClick={() => changeBucket(entry)}>
              {BUCKET_LABEL[entry]}
              <Badge ml='6px' colorScheme={BUCKET_COLOR_SCHEME[entry]} variant='solid' borderRadius='full'>
                {counts[entry]}
              </Badge>
            </Button>
          ))}
        </HStack>

        {!readOnly && bucket === 'decide' && highConfidenceCount > 0 && (
          <Button size='sm' variant='outline' colorScheme='green' isLoading={isSaving} onClick={acceptHighConfidence}>
            Accept {highConfidenceCount} exact matches
          </Button>
        )}
      </Flex>

      {!readOnly && selected.size > 0 && (
        <Flex
          align='center'
          justify='space-between'
          gap='12px'
          mb='8px'
          p='8px 12px'
          bg='red.50'
          border='1px solid'
          borderColor='red.100'
          borderRadius='8px'>
          <Text fontSize='sm' fontWeight='medium'>
            {selected.size} selected on this page
          </Text>
          <HStack spacing='6px'>
            <Button size='xs' colorScheme='green' isLoading={isSaving} onClick={() => bulk('approved')}>
              Approve
            </Button>
            <Button size='xs' variant='outline' isLoading={isSaving} onClick={() => bulk('skipped')}>
              Hold
            </Button>
            <Button size='xs' variant='ghost' onClick={() => setSelected(new Set())}>
              Clear
            </Button>
          </HStack>
        </Flex>
      )}

      {isLoading && (
        <Text fontSize='sm' color='gray.500'>
          Loading rows…
        </Text>
      )}

      {!isLoading && visible.length === 0 && (
        <Flex align='center' justify='center' py='40px'>
          <Text fontSize='sm' color='gray.500'>
            Nothing in {BUCKET_LABEL[bucket].toLowerCase()}.
          </Text>
        </Flex>
      )}

      {!isLoading && visible.length > 0 && (
        <Box overflowX='auto' border='1px solid' borderColor='gray.200' borderRadius='8px'>
          <Table size='sm' variant='simple'>
            <Thead bg='gray.50' position='sticky' top='0' zIndex={1}>
              <Tr>
                <Th width='36px'>
                  <Checkbox
                    isChecked={visible.length > 0 && selected.size === visible.length}
                    isIndeterminate={selected.size > 0 && selected.size < visible.length}
                    isDisabled={readOnly}
                    onChange={toggleAll}
                    aria-label='select-all-rows'
                  />
                </Th>
                <Th>Source line</Th>
                <Th>Matched product</Th>
                <Th>Match</Th>
                <Th>Price</Th>
                <Th>vs current</Th>
                <Th>State</Th>
                <Th width='84px'>Actions</Th>
              </Tr>
            </Thead>
            <Tbody>
              {visible.map((row) => (
                <ImportRowLine
                  key={row.id}
                  row={row}
                  readOnly={readOnly}
                  isSelected={selected.has(row.id)}
                  isExpanded={expandedRowId === row.id}
                  onToggleSelect={toggle}
                  onToggleExpand={toggleExpand}
                  onPriceCommit={commitPrice}
                  onSetStatus={setStatus}
                  onPickMaster={pickMaster}
                />
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
};
